import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  Signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import {
  DateRangeFormComponent,
  NgCommonModule,
  validFormGroupChanges$,
} from '@principle-theorem/ng-shared';
import {
  IQueryableTimestamp,
  IReportBuilderDataSource,
} from '@principle-theorem/reporting/interfaces';
import { filterUndefined, ITimePeriod } from '@principle-theorem/shared';
import { take } from 'rxjs/operators';
import {
  IReportBuilderDateRangeFormData,
  ReportBuilderDateRangeForm,
} from './report-builder-date-range';

@Component({
  selector: 'pr-report-builder-date-range-form',
  templateUrl: './report-builder-date-range-form.component.html',
  styleUrl: './report-builder-date-range-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatTooltip,
    NgCommonModule,
    DateRangeFormComponent,
  ],
})
export class ReportBuilderDateRangeFormComponent {
  formGroup = new ReportBuilderDateRangeForm();
  queryableTimestamps: Signal<IQueryableTimestamp[]>;

  dataSource = input.required<IReportBuilderDataSource>();
  dateRange = input<IReportBuilderDateRangeFormData>();
  dateRangeChange = output<IReportBuilderDateRangeFormData>();

  constructor() {
    this.queryableTimestamps = computed(
      () => this.dataSource()?.queryableTimestamps ?? []
    );

    toObservable(this.dateRange)
      .pipe(filterUndefined(), take(1), takeUntilDestroyed())
      .subscribe((data) =>
        this.formGroup.patchValue(data, { emitEvent: false })
      );

    toObservable(this.queryableTimestamps)
      .pipe(takeUntilDestroyed())
      .subscribe((queryableTimestamps) => {
        const timestampIds = queryableTimestamps.map(
          (queryableTimestamp) => queryableTimestamp.property.metadata.id
        );
        if (!timestampIds.includes(this.formGroup.value.queryableTimestamp)) {
          this.formGroup.patchValue({ queryableTimestamp: timestampIds[0] });
        }
      });

    validFormGroupChanges$(this.formGroup)
      .pipe(takeUntilDestroyed())
      .subscribe((data) => this.dateRangeChange.emit(data));
  }

  setDateRange(dateRange: ITimePeriod): void {
    this.formGroup.patchValue(dateRange, { emitEvent: true });
  }
}
